import React from 'react';
import Spinner from './Spinner';
import KeyStrengthMeter from './KeyStrengthMeter';
import ExplainButton from './ExplainButton';
import CommonMistakesWarning from './CommonMistakesWarning';

function Controls({
  algorithm,
  setAlgorithm,
  rsaBits,
  setRsaBits,
  rsaHash,
  setRsaHash,
  ecCurve,
  setEcCurve,
  keyUse,
  setKeyUse,
  alg,
  onGenerate,
  onClear,
  busy,
  message
}) {
  const isRSA = algorithm === 'RSA';

  return (
    <section className="controls card">
      <h2>Generate Keys</h2>
      <div className="controls-grid">
        <div className="field">
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
            <label htmlFor="kind">Algorithm</label>
          </div>
          <select 
            id="kind" 
            value={algorithm} 
            onChange={(e) => setAlgorithm(e.target.value)}
          >
            <option value="RSA">RSA</option>
            <option value="EC">EC (ECDSA)</option>
          </select>
           <ExplainButton concept={algorithm.toLowerCase()} compact={true} />
        </div>

        {isRSA && (
          <>
            <div className="field">
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                <label htmlFor="rsaBits">Modulus</label>
              </div>
              <select 
                id="rsaBits" 
                value={rsaBits} 
                onChange={(e) => setRsaBits(e.target.value)}
              >
                <option value="2048">2048</option>
                <option value="3072">3072</option>
                <option value="4096">4096</option>
              </select>
            </div>

            <div className="field">
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                <label htmlFor="rsaHash">Hash</label>
              </div>
              <select 
                id="rsaHash" 
                value={rsaHash} 
                onChange={(e) => setRsaHash(e.target.value)}
              >
                <option>SHA-256</option>
                <option>SHA-384</option>
                <option>SHA-512</option>
              </select>
            </div>
          </>
        )}

        {!isRSA && (
          <div className="field">
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
              <label htmlFor="ecCurve">Curve</label>
            </div>
            <select 
              id="ecCurve" 
              value={ecCurve} 
              onChange={(e) => setEcCurve(e.target.value)}
            >
              <option>P-256</option>
              <option>P-384</option>
              <option>P-521</option>
            </select>
          </div>
        )}

        <div className="field">
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
            <label htmlFor="keyUse">Key Use</label>
          </div>
          <select 
            id="keyUse" 
            value={keyUse} 
            onChange={(e) => setKeyUse(e.target.value)}
          >
            <option value="sig">Signature (sig)</option>
            <option value="enc">Encryption (enc)</option>
          </select>
          <ExplainButton concept="key-use" compact={true} />
        </div>

        <div className="field">
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
            <label>alg</label>
          </div>
          <div className="badge">{alg}</div>
        </div>
      </div>

      <div className="actions">
        <button 
          className="btn primary" 
          onClick={onGenerate} 
          disabled={busy}
        >
          {busy && <Spinner size={16} />}
          Generate keypair
        </button>
        <button 
          className="btn" 
          onClick={onClear}
        >
          Clear
        </button>
        <span className="muted">{message}</span>
      </div>

      {/* Key Strength Analysis */}
      <KeyStrengthMeter 
        keyType={algorithm}
        keySize={isRSA ? parseInt(rsaBits) : null}
        curve={!isRSA ? ecCurve : null}
        showRecommendations={true}
      />

      {/* Common Mistakes Warning */}
      <CommonMistakesWarning
        algorithm={algorithm}
        keyUse={keyUse}
        rsaBits={rsaBits}
        curve={ecCurve}
        context="generation"
      />
    </section>
  );
}

export default Controls;